/**
 * @file 習い事・体験のいまの状況 (読み取り専用カード)。
 *
 * @what: 「かよっているもの」(まとまりごとの絵文字 + 名前 + 教室メモ) と「いま動いていること」、
 *   参照元 (And Lives の「道すじ」) へのリンクと情報の日付を出す。
 * @why: 習い事・体験の検討と記録の正本は And Lives 側にあり、ここでは学習の計画と
 *   並べて眺められるだけの要約を出す (代表 2026-08-20)。編集 UI は持たない。
 * @failure: 描画のみ。データが無いケース (未同期) は親が呼び出し自体を出し分ける。
 */

import type { StudyExploration } from "../api/exploration";
import { formatSourceUpdated } from "../api/exploration";
import "./study.css";

export function ExplorationCard({ exploration }: { exploration: StudyExploration }) {
  const hasClusters = exploration.clusters.length > 0;
  const hasActions = exploration.actions.length > 0;
  return (
    <section className="study-exploration-card">
      <div>
        <h3 className="study-card-title">通っているもの</h3>
        {hasClusters ? (
          <ul className="study-exploration-list">
            {exploration.clusters.map((cluster) => (
              <li key={cluster.label} className="study-exploration-row">
                <span className="study-exploration-cluster">
                  {cluster.emoji ? <span aria-hidden="true">{cluster.emoji} </span> : null}
                  {cluster.label}
                </span>
                <ul className="study-exploration-items">
                  {cluster.items.map((item) => (
                    <li key={item.label}>
                      <span className="study-exploration-item-label">{item.label}</span>
                      {item.detail ? (
                        <span className="study-exploration-item-detail">{item.detail}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        ) : (
          <p className="study-muted">いま定期的に通っているものはありません。</p>
        )}
      </div>

      {hasActions ? (
        <div>
          <h3 className="study-card-title">いま動いていること</h3>
          <ul className="study-exploration-actions">
            {exploration.actions.map((action) => (
              <li key={action.title}>
                {action.tag ? <span className="study-exploration-tag">{action.tag}</span> : null}
                <span>{action.title}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="study-exploration-footer">
        {exploration.site_url ? (
          <a href={exploration.site_url} target="_blank" rel="noreferrer noopener">
            And Lives の「道すじ」を開く
            <span aria-hidden="true"> ↗</span>
          </a>
        ) : null}
        {exploration.source_updated ? (
          <span className="study-muted">
            {formatSourceUpdated(exploration.source_updated)}時点の情報
          </span>
        ) : null}
      </p>
    </section>
  );
}
